import GridPage from "components/layout/GridPage";
import { Alert, Box, IconButton, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import useIsMobile from "components/hooks/useIsMobile";
import { useLocation } from "react-router-dom";
import { useWebSocketContext } from "components/context/SocketConsumerProvider";
import {
  useStartTaskAgent,
  useAnswerAgentQuestion,
  useAgentMessage,
} from "services/agents";
import { useTaskAgentState } from "components/hooks/useTaskAgentState";
import TaskAgentDisplay from "components/tools/TaskAgentDisplay";
import { ArrowUp } from "lucide-react";
import AdminFamilySelect from "components/creation/AdminFamilySelect";
import Loading from "components/common/Loading";

export default () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isMobile = useIsMobile();
  const [title, setTitle] = useState(queryParams.get("title") || "");
  const [description, setDescription] = useState(
    queryParams.get("description") || "",
  );
  const { registerHandler, unregisterHandler } = useWebSocketContext();
  const { request, loading, error } = useStartTaskAgent();
  const [hasStarted, setHasStarted] = useState(false);
  const [hasActivated, setHasActivated] = useState(false);
  const { request: answerQuestion } = useAnswerAgentQuestion();
  const { request: agentMessage } = useAgentMessage();
  const [selectedFamily, setSelectedFamily] = useState<string | null>(null);
  const [currentThreadRef, setCurrentThreadRef] = useState<string | null>(null);
  const stateManager = useTaskAgentState();

  useEffect(() => {
    const channel = "";
    registerHandler(channel, stateManager.actions.handleWebSocketMessage);

    return () => {
      unregisterHandler(channel, stateManager.actions.handleWebSocketMessage);
    };
  }, [
    registerHandler,
    unregisterHandler,
    stateManager.actions.handleWebSocketMessage,
  ]);

  useEffect(() => {
    if (stateManager.state.isActive) {
      setHasActivated(true);
    }
  }, [stateManager.state.isActive]);

  const onSubmit = async () => {
    if (!selectedFamily || (!title && !description)) {
      return;
    }
    setHasStarted(true);

    // Combine title and description for the message
    let combinedMessage = "Help me solve this task: ";
    if (title) {
      combinedMessage += title;
    }
    if (description) {
      combinedMessage += title ? `\n\n${description}` : description;
    }

    const response = await request({
      familyRef: selectedFamily || "",
      message: combinedMessage,
    });
    if (response?.threadRef) {
      setCurrentThreadRef(response.threadRef);
    }
  };

  const handleAnswerQuestions = async (
    answers: Array<{ functionId: string; answer: string }>,
  ) => {
    if (!currentThreadRef) {
      console.error("No thread reference available");
      return;
    }

    await answerQuestion({
      threadRef: currentThreadRef,
      answers: answers.map((answer) => ({
        functionId: answer.functionId,
        text: answer.answer,
      })),
    });
  };

  const handleFollowUpQuestion = async (question: string) => {
    if (!currentThreadRef) {
      console.error("No thread reference available");
      return;
    }
    // Send the follow-up question as a new message in the same thread
    await agentMessage({
      threadRef: currentThreadRef,
      message: question,
    });
  };

  return (
    <GridPage
      sx={{
        margin: isMobile ? "" : "64px auto",
        maxWidth: "800px",
        padding: isMobile ? "20px" : "0",
      }}
    >
      <Typography variant="title" id="tableTitle">
        Task Agent
      </Typography>
      <Box
        sx={{
          marginTop: "24px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          maxWidth: "800px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: "8px",
              alignItems: "start",
            }}
          >
            <AdminFamilySelect
              sx={{ maxWidth: "150px" }}
              selectedFamily={selectedFamily}
              onChange={setSelectedFamily}
              title="Select Family"
              disabled={loading || hasStarted}
            />
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}
            >
              <TextField
                label="Task Title"
                variant="outlined"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoComplete="off"
                disabled={loading || hasStarted}
                fullWidth
                size="small"
                onClick={(event) => event.stopPropagation()}
              />
              <TextField
                label="Description (optional)"
                variant="outlined"
                placeholder="Additional details or context"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                autoComplete="off"
                disabled={loading || hasStarted}
                fullWidth
                multiline
                rows={1}
                size="small"
                onClick={(event) => event.stopPropagation()}
              />
            </Box>
            <IconButton
              onClick={onSubmit}
              disabled={
                loading ||
                (!title && !description) ||
                !selectedFamily ||
                hasStarted
              }
              sx={{
                alignSelf: "flex-end",
                marginBottom: "8px",
                borderRadius: "30px",
                background: "black",
                "&:hover": {
                  background: "black",
                  opacity: 0.8,
                },
                "&:disabled": {
                  background: "black",
                  opacity: 0.5,
                },
                padding: "8px",
              }}
            >
              <ArrowUp size={26} color="white" />
            </IconButton>
          </Box>
          {error && (
            <Alert severity="error" sx={{ padding: "12px 24px" }}>
              Error starting agent.
            </Alert>
          )}
        </Box>
        {hasStarted && !hasActivated && <Loading />}
        <TaskAgentDisplay
          stateManager={stateManager}
          onAnswerQuestions={handleAnswerQuestions}
          onFollowUpQuestion={handleFollowUpQuestion}
        />
      </Box>
    </GridPage>
  );
};
